import { Schema } from "effect";
import { DocumentId } from "./ids.ts";

// The curriculum altitudes, derived from heading structure (never stored as
// records). `#`=curriculum, `##`=unit-plan, `###`=cycle, `####`=module, and the
// single `##### Authored Exercises` heading a populated module carries is the
// `exercise` layer (its exercises are numbered list items inside it, not headings
// of their own). A Level (CEFR band) is a computed grouping of unit-plans, never a
// heading.
//
// `appendix` (CON-927) is the sixth member and the only one depth does NOT decide: it applies to the
// terminal `## Appendix` and to EVERY heading beneath it, whatever their depth. The studio's Section
// model is a DOCUMENT model, so the appendix and its sub-sections stay first-class here — they appear
// in `read-outline` and resolve via `read-block` — where the curriculum model
// (`packages/curriculum-md`'s strict parse, and codexflow-schemas downstream) truncates at it and
// materialises nothing. That asymmetry is the decision; see ADR-0075/ADR-0087's CON-927 amendments.
export const Altitude = Schema.Literal(
  "curriculum",
  "unit-plan",
  "cycle",
  "module",
  "exercise",
  "appendix",
);
export type Altitude = typeof Altitude.Type;

// Markdown heading depth: `#`=1 … `####`=4, plus `#####`=5 for the module's single
// `##### Authored Exercises` heading (the exercise layer).
export const HeadingDepth = Schema.Literal(1, 2, 3, 4, 5);
export type HeadingDepth = typeof HeadingDepth.Type;

// A heading-bounded region: from its heading line to the line before the next
// heading of same-or-higher depth. Line numbers are 1-based and inclusive.
// `lead` is the block's first paragraph (a concise authored summary) — label-stripped
// and budget-capped, absent when the heading has no leading paragraph (see `sectionsOf`).
export const Section = Schema.Struct({
  title: Schema.String,
  depth: HeadingDepth,
  altitude: Altitude,
  lineStart: Schema.Int.pipe(Schema.positive()),
  lineEnd: Schema.Int.pipe(Schema.positive()),
  lead: Schema.optional(Schema.String),
});
export type Section = typeof Section.Type;

// The heading skeleton of a Curriculum: an ordered list of sections. `read-outline` hard-bounds the
// response size regardless of arguments (maxDepth is advisory, not a size guarantee): the returned
// `sections` are a byte-bounded page into the full skeleton, and `nextCursor` (a 0-based offset into
// the flat, depth-filtered section list) is present when more sections remain — resume by passing it
// back as `cursor`. A single call therefore can never overflow the tool-result cap even at full depth
// on a large curriculum.
export const Outline = Schema.Struct({
  documentId: DocumentId,
  sections: Schema.Array(Section),
  nextCursor: Schema.optional(Schema.Int.pipe(Schema.nonNegative())),
});
export type Outline = typeof Outline.Type;
